/**
 * Solo Mode "Football Islands" — Level play screen.
 *
 * Fetches /api/solo/level/:n (3 clues + difficulty_band, no correct
 * player). Renders three large FUT-style clue cards stacked vertically.
 * The user picks a player via autocomplete on /api/solo/player-search,
 * then POSTs to /api/solo/answer.
 *
 * Result handling per Phase 2 brief:
 *   - correct → confetti + reveal + "Next level" CTA
 *   - wrong (lives > 0)  → shake + toast + decrement lives
 *   - wrong (lives == 0) → reveal + "Out of lives" gate
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Search, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

import { api } from "../lib/api";
import SoloLivesBadge from "../components/SoloLivesBadge";

function ClueCard({ index, text }) {
  return (
    <motion.div
      data-testid={`solo-clue-${index + 1}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 + 0.05, duration: 0.4 }}
      className="relative rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-black p-6 sm:p-8 shadow-[0_15px_45px_-15px_rgba(0,255,135,0.6)]"
    >
      <div className="absolute top-3 right-4 text-[10px] uppercase tracking-[0.4em] font-black opacity-60">
        Clue {index + 1}
      </div>
      <div className="absolute top-3 left-4 w-7 h-7 rounded-full bg-black text-[var(--accent)] flex items-center justify-center text-xs font-black">
        {index + 1}
      </div>
      <p className="text-lg sm:text-xl font-extrabold uppercase tracking-tight leading-tight mt-3"
         style={{ textShadow: "0 2px 0 rgba(0,0,0,0.18)" }}>
        {text}
      </p>
    </motion.div>
  );
}

function PlayerSearchBox({ disabled, onPick, picked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef(null);

  const search = useCallback((term) => {
    if (!term.trim()) {
      setResults([]);
      return;
    }
    api.get(`/solo/player-search?q=${encodeURIComponent(term)}`)
       .then((r) => setResults(r.data || []))
       .catch(() => setResults([]));
  }, []);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => search(q), 200);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [q, search]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-xl bg-black border border-white/10 focus-within:border-[var(--accent)] transition px-4 py-3">
        <Search size={16} className="text-zinc-500" />
        <input
          data-testid="solo-search-input"
          value={picked ? picked.name : q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            if (picked) onPick(null);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          disabled={disabled}
          placeholder="Type a player name…"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-zinc-600"
        />
        {(picked || q) && !disabled && (
          <button
            data-testid="solo-search-clear"
            onClick={() => { setQ(""); onPick(null); }}
            className="text-zinc-500 hover:text-zinc-200"
          >
            <X size={16} />
          </button>
        )}
      </div>
      {open && results.length > 0 && !picked && (
        <div className="absolute z-20 mt-1 w-full rounded-xl bg-zinc-900 border border-white/10 max-h-72 overflow-y-auto shadow-xl">
          {results.map((p) => (
            <button
              key={p._id}
              data-testid={`solo-search-result-${p._id}`}
              onClick={() => { onPick(p); setOpen(false); setQ(""); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 transition text-left"
            >
              <div className="w-9 h-9 rounded-full bg-zinc-800 overflow-hidden flex items-center justify-center text-xs text-zinc-500 shrink-0">
                {p.image_url ? (
                  <img alt="" src={p.image_url} className="w-full h-full object-cover" />
                ) : (
                  p.name.split(" ").map((s) => s[0]).slice(0, 2).join("")
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{p.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {p.nationality} · {p.position}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function fireConfetti() {
  const burst = (origin) =>
    confetti({
      particleCount: 90,
      spread: 70,
      startVelocity: 45,
      origin,
      colors: ["#00ff87", "#10b981", "#facc15", "#fbbf24"],
    });
  burst({ x: 0.5, y: 0.6 });
  setTimeout(() => burst({ x: 0.2, y: 0.7 }), 200);
  setTimeout(() => burst({ x: 0.8, y: 0.7 }), 400);
}

export default function SoloLevel() {
  const { n } = useParams();
  const level = parseInt(n, 10);
  const navigate = useNavigate();
  const [puzzle, setPuzzle] = useState(null);
  const [livesData, setLivesData] = useState(null);
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { correct, correct_player?, lives_remaining, max_unlocked_level? }
  const [shake, setShake] = useState(0);
  const [loadError, setLoadError] = useState(null);

  // After a correct answer the server returns `max_unlocked_level`. We pass
  // that (plus latest lives) to the map on navigation so the map renders the
  // unlocked state immediately — no flicker waiting for a re-fetch.
  const mapNavState = () => ({
    progress: result?.correct
      ? { max_unlocked_level: result.max_unlocked_level, cleared_levels: [level], total_levels: 100 }
      : undefined,
    lives: livesData,
  });

  useEffect(() => {
    let cancelled = false;
    setResult(null);
    setPicked(null);
    setPuzzle(null);
    Promise.all([
      api.get(`/solo/level/${level}`),
      api.get("/solo/lives"),
    ])
      .then(([pRes, lRes]) => {
        if (cancelled) return;
        setPuzzle(pRes.data);
        setLivesData(lRes.data);
      })
      .catch((e) => !cancelled && setLoadError(e?.response?.data?.detail || "failed"));
    return () => { cancelled = true; };
  }, [level]);

  const submit = async () => {
    if (!picked || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/solo/answer", {
        level,
        guessed_player_id: picked._id,
      });
      setResult(data);
      // refresh lives badge after server-side decrement
      const lr = await api.get("/solo/lives");
      setLivesData(lr.data);
      if (data.correct) {
        fireConfetti();
      } else if (data.lives_remaining > 0) {
        toast.error("Wrong — −1 life", { id: "wrong" });
        setShake((x) => x + 1);
        // allow another attempt — clear the local result so the input re-enables
        setTimeout(() => {
          setResult(null);
          setPicked(null);
        }, 1200);
      }
      // If lives_remaining == 0, leave result on screen (reveal + out-of-lives gate)
    } catch (e) {
      const detail = e?.response?.data?.detail || "submission failed";
      if (e?.response?.status === 429) {
        toast.error("Out of lives — wait for regen");
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div data-testid="solo-level-error" className="min-h-screen bg-[var(--bg)] text-zinc-400 flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-sm uppercase tracking-[0.3em] mb-2">Cannot open level {level}</div>
          <div className="text-xs text-zinc-500">{String(loadError)}</div>
          <Link to="/solo" className="inline-block mt-4 px-4 py-2 rounded-full border border-white/15 text-sm">Back to map</Link>
        </div>
      </div>
    );
  }
  if (!puzzle || !livesData) {
    return (
      <div data-testid="solo-level-loading" className="min-h-screen bg-[var(--bg)] text-zinc-400 flex items-center justify-center">
        <div className="text-xs uppercase tracking-[0.3em]">loading level {level}…</div>
      </div>
    );
  }

  const outOfLives = result && !result.correct && result.lives_remaining === 0;

  return (
    <div data-testid="solo-level-page" className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="sticky top-0 z-30 backdrop-blur bg-black/60 border-b border-white/10">
        <div className="max-w-2xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link
            to="/solo"
            data-testid="solo-level-back"
            state={mapNavState()}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5 transition text-sm"
          >
            <ArrowLeft size={14} /> Map
          </Link>
          <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">
            Level <span data-testid="solo-level-num" className="text-[var(--accent)] font-black">#{level}</span>
            <span className="ml-2 opacity-50">· {puzzle.difficulty_band}</span>
          </div>
          <SoloLivesBadge
            lives={livesData.lives}
            nextRegenInSeconds={livesData.next_regen_in_seconds}
          />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8 space-y-5">
        {/* Clue cards */}
        <motion.div
          key={shake}
          animate={shake > 0 ? { x: [0, -12, 12, -8, 8, 0] } : undefined}
          transition={{ duration: 0.45 }}
          className="space-y-4"
        >
          {puzzle.clues.map((c, i) => (
            <ClueCard key={i} index={i} text={c} />
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          {result?.correct ? (
            <motion.div
              key="correct"
              data-testid="solo-result-correct"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="rounded-2xl border-2 border-[var(--accent)] bg-gradient-to-br from-emerald-950 to-black p-6 text-center"
            >
              <Sparkles size={32} className="mx-auto text-[var(--accent)]" />
              <div className="mt-3 text-xs uppercase tracking-[0.4em] text-emerald-300">Correct!</div>
              <div className="mt-2 text-3xl font-black">{result.correct_player?.name}</div>
              {result.correct_player?.image_url && (
                <img
                  alt={result.correct_player.name}
                  src={result.correct_player.image_url}
                  className="mx-auto mt-4 w-28 h-28 rounded-full object-cover border-4 border-[var(--accent)]"
                />
              )}
              <button
                data-testid="solo-next-level"
                onClick={() => navigate(`/solo/level/${level + 1}`, { state: mapNavState() })}
                disabled={level >= 100}
                className="mt-6 px-6 py-3 rounded-full bg-[var(--accent)] text-black font-black hover:brightness-110 disabled:opacity-30 transition"
              >
                {level >= 100 ? "All islands cleared 🏆" : "Next level →"}
              </button>
            </motion.div>
          ) : outOfLives ? (
            <motion.div
              key="out"
              data-testid="solo-result-out-of-lives"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-rose-500/40 bg-rose-500/5 p-6 text-center"
            >
              <div className="text-xs uppercase tracking-[0.4em] text-rose-400">Out of lives</div>
              <div className="mt-2 text-lg font-bold">The answer was</div>
              <div className="mt-1 text-3xl font-black text-rose-300">{result.correct_player?.name}</div>
              <div className="mt-4">
                <SoloLivesBadge lives={0} nextRegenInSeconds={livesData.next_regen_in_seconds} />
              </div>
              <Link
                to="/solo"
                data-testid="solo-back-to-map"
                state={mapNavState()}
                className="inline-block mt-5 px-5 py-2.5 rounded-full border border-white/15 text-sm hover:bg-white/5"
              >
                Back to map
              </Link>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              <PlayerSearchBox
                disabled={submitting || (livesData.lives <= 0)}
                onPick={setPicked}
                picked={picked}
              />
              <button
                data-testid="solo-submit"
                onClick={submit}
                disabled={!picked || submitting || livesData.lives <= 0}
                className="w-full py-3 rounded-xl bg-[var(--accent)] text-black font-black hover:brightness-110 disabled:opacity-30 transition"
              >
                {submitting ? "Submitting…" : livesData.lives <= 0 ? "Out of lives" : "Submit guess"}
              </button>
              {livesData.lives <= 0 && (
                <div data-testid="solo-lives-zero-hint" className="text-xs text-rose-300 text-center">
                  Wait for regen, then come back to try again.
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
